import * as React from 'react';
import type { ParentStudyViewModel } from '../../../Semester/ParentStudyViewModel';
import type { OnlineStudySubject } from '../../../ViewModel/Subjects/OnlineStudySubject.csharp';
import { OnlineStudyPlanContext } from '../../Context/OnlineStudyPlanContext';
import { IsChoiceAddedToBasket } from '../../Context/OnlineStudyPlanHelpers';
import { Txt } from '../../Txt';
import { Link } from '~/Features/Partials/Link/Link';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { moneyToString } from '~/Partials/Utils/utils';
import styles from './SingleSubject.module.css';

interface SubjectProps {
    subject: OnlineStudySubject;
    parentStudy: ParentStudyViewModel;
}

export const Subject: React.FC<React.PropsWithChildren<SubjectProps>> = ({ subject, parentStudy }) => {
    const { choicesFromBasket, addChoicesToBasketHandler, removeChoicesFromBasketHandler } =
        React.useContext(OnlineStudyPlanContext);

    const addChoiceToBasket = () => {
        addChoicesToBasketHandler({
            subject,
            parentStudy,
        });
    };
    const removeChoiceFromBasket = () => {
        removeChoicesFromBasketHandler({
            subject,
            parentStudy,
        });
    };

    const isAddedToTheBasket = IsChoiceAddedToBasket(choicesFromBasket, {
        subject,
        parentStudy,
    });

    return (
        <li key={subject.code} className={styles.SubjectItem}>
            <div className={styles.ItemSummary}>
                <div className={styles.ItemName}>
                    <div className={styles.LinkContainer}>
                        <Link href={subject.url} label={subject.name} className={styles.Link} />
                    </div>
                </div>
                <div className={styles.ItemInfo}>
                    <span className={styles.ItemPoints}>
                        {subject.credits} {subject.creditsLabel}
                    </span>
                    <span className={styles.ItemPrice}>{moneyToString(subject.price)}</span>
                </div>
            </div>
            <div>
                {isAddedToTheBasket ? (
                    <Button
                        className={styles.RemoveButton}
                        onClick={removeChoiceFromBasket}
                        size={ButtonSize.Small}
                        type={ButtonType.Outline}
                        tier={ButtonTier.LegacyPrimary}
                    >
                        <Txt id="remove" />
                    </Button>
                ) : (
                    <Button
                        className={styles.OrderButton}
                        onClick={addChoiceToBasket}
                        size={ButtonSize.Small}
                        type={ButtonType.Solid}
                        tier={ButtonTier.LegacyPrimary}
                    >
                        <Txt id="order" />
                    </Button>
                )}
            </div>
        </li>
    );
};
